import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title:
    "TMS for Treatment-Resistant Depression | Neuro Wellness TMS Centers of America",
  description:
    "Treatment-resistant depression: learn how TMS, SAINT accelerated TMS, and SPRAVATO may help when antidepressants haven't worked.",
  canonical: "/tms-for-treatment-resistant-depression",
  keywords:
    "TMS treatment-resistant depression, TRD TMS therapy, treatment-resistant depression Florida, SAINT TRD, SPRAVATO TRD",
};

const relatedLinks = [
  { title: "TMS for Depression", href: "/services/depression" },
  { title: "SAINT TMS", href: "/saint" },
  { title: "SPRAVATO Overview", href: "/services/spravato" },
  { title: "Compare Treatments", href: "/compare-treatments" },
  { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
];

export default function TmsForTreatmentResistantDepression() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Treatment-Resistant Depression"
      title="TMS for Treatment-Resistant Depression"
      subtitle="If you've tried two or more antidepressants without adequate relief, TMS may be an option. Here's what you need to know."
    >
      <DecisionSection heading="What treatment-resistant depression is">
        <p>
          Treatment-resistant depression (TRD) is not a failure on your part.
          It's a clinical category describing depression that hasn't responded
          adequately to at least two antidepressants from different classes,
          taken at adequate doses for a sufficient duration.
        </p>
        <p>
          About a third of people with depression don't get adequate relief from
          oral antidepressants. That's not a small unlucky minority — it's a
          known limitation of how those medications work, and treatments were
          developed specifically for this situation.
        </p>
      </DecisionSection>

      <DecisionSection heading="How TMS is different">
        <p>
          TMS works through a different mechanism than oral antidepressants.
          Instead of changing chemical signaling throughout your body, TMS uses
          focused magnetic pulses to stimulate specific areas of the brain
          involved in mood regulation — primarily the dorsolateral prefrontal
          cortex.
        </p>
        <p>
          It's FDA-cleared, non-invasive, and performed in an outpatient
          setting. You sit in a chair, stay awake, and can drive yourself home
          afterward.
        </p>
      </DecisionSection>

      <DecisionSection heading="Treatment options for TRD">
        <DecisionList
          items={[
            "Standard TMS — sessions 5 days/week for ~6 weeks, FDA-cleared",
            "SAINT accelerated TMS — 5-day protocol with fMRI targeting, FDA-cleared",
            "SPRAVATO (esketamine) — FDA-approved nasal spray for TRD",
            "IV ketamine infusions — off-label but widely used",
            "Ketamine-assisted psychotherapy (KAP)",
            "Medication management — adjusting or augmenting current medications",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          A consultation is the place to sort through which treatment may be
          right for you. We'll review your history, what you've tried, and what
          your insurance may cover.
        </p>
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link to="/contactus" className="text-accent hover:underline">
            schedule a consultation
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
